import { api } from '../api.js';
import { getUser } from '../auth.js';
import { showToast, openModal, closeModal } from '../ui.js';
import { escapeHtml, sprintOptionsHtml, nextSprintNumber, renderMarkdown } from '../utils.js';
import { renderMemberCombo, createMemberCombo } from './member-combo.js';
import { attachImageHandlers } from '../lib/image-paste.js';
import { attachEmbedControls } from '../lib/embed-menu.js';
import { openTrelloCardPicker } from './trello-card-picker.js';
import { openGmailThreadPicker } from './gmail-thread-picker.js';
import { openJiraIssuePicker } from './jira-issue-picker.js';
import { closeImportPicker } from '../lib/import-picker-shell.js';
import { attachTagTypeahead } from '../lib/tag-typeahead.js';
import { attachDependencyTypeahead } from './dependency-typeahead.js';
import { loadNewTaskDraft, saveNewTaskDraft, clearNewTaskDraft, attachmentDeletePath } from '../lib/task-draft.js';
import { groupLabelSingular } from '../lib/task-group-label.js';
import { sprintsEnabled, defaultSprintNumber } from '../lib/sprints-enabled.js';
import { loadProject } from '../lib/project.js';
import { loadStatuses, roleStatus, statusLabel } from '../lib/statuses.js';
import { renderParentContext } from './parent-context.js';

// ── Create task modal ──

// applyImportPrefill(overlay, { title, description }) — writes a picked import source's
// content (Trello card — C1448, Gmail thread — C1449) into the still-open New Task modal.
// Overwrites both fields (user decision — no merge with anything already typed).
function applyImportPrefill(overlay, { title, description }) {
  const titleEl = overlay.querySelector('#task-title');
  const descEl = overlay.querySelector('#task-desc');
  const previewEl = overlay.querySelector('#task-desc-preview');
  if (!titleEl || !descEl || !titleEl.isConnected) return; // modal closed while the fetch was in flight

  // maxlength="255" on #task-title is NOT enforced for a programmatic .value write, and the
  // API hard-rejects a >255-char title (400) on create. Truncate defensively so imported
  // source data cannot turn a valid modal submission into a validation error.
  if (title) {
    titleEl.value = title.slice(0, 255);
    // (C1512) titleEl carries its own 'input' -> persistDraft() listener (see
    // openCreateTaskModal's onMount below) — a programmatic .value write doesn't fire
    // 'input' on its own, so an imported card with a title but no description would
    // otherwise never get saved into the draft.
    titleEl.dispatchEvent(new Event('input', { bubbles: true }));
  }
  if (description) {
    descEl.value = description;
    // One synthetic input drives both C1446's preview re-render (the listener a few lines
    // below) and C1447's thumbnail strip (embed-menu.js's own 'input' listener).
    descEl.dispatchEvent(new Event('input', { bubbles: true }));
    // C1446 pins previewEl.style.height on every blur and never clears it; the preview box
    // is overflow-y:auto, so without clearing it here the freshly-imported (much longer)
    // content renders scrolled out of sight behind the old, smaller pinned height. C1446
    // re-pins it on the next blur, so its own button-jump protection is unaffected.
    if (previewEl && !previewEl.hidden) previewEl.style.height = '';
  }
}

// initialSprintPriority(priority, sprints, draftPriority) — the sprint the modal starts on.
// `defaultPriority` is what the hidden field submits (sprints off) and what an unparseable
// select saves into the draft; `restorePriority` is what the visible select pre-selects.
// A `priority` > 0 is an explicit sprint (a lane click, a scheduled parent) and beats a saved
// draft. 0 — a backlog parent, since sprintOptionsHtml() lists no 0 option — or absent means
// "no explicit sprint": both fall back to the newest sprint, so the visible pre-selection and
// the hidden submit can never disagree.
export function initialSprintPriority(priority, sprints, draftPriority) {
  const explicit = Number.isFinite(priority) && priority > 0;
  const defaultPriority = explicit ? priority : defaultSprintNumber(sprints);
  const restorePriority = explicit || draftPriority == null ? defaultPriority : draftPriority;
  return { defaultPriority, restorePriority };
}

// opts.priority (TPT30) — an explicit sprint to prefill, e.g. from the sprint board's own
// per-lane "+ Add item" tile (sprint-board.js's onAddTask), or a parent's sprint (epic Add
// Item, drawer Add Child). Omitted for the header's "+ New Task" button; omitted and 0 (a
// backlog parent) both keep the default-newest-sprint behavior — see initialSprintPriority().
//
// Resolves to the modal's owner token (ui.js openModal(), TPT647) for the caller's teardown, or
// null when it never opened (form data failed to load, or the caller's page is gone).
export async function openCreateTaskModal(projectId, opts = {}) {
  // Capture per-open context; late responses must never read a different page's state.
  const routePath = () => window.location.hash.split('?')[0];
  const initialRoute = routePath();
  const isAlive = () => routePath() === initialRoute && (!opts.isAlive || opts.isAlive());
  let state;
  try {
    const [project, sprintRows, members, sprints, statuses] = await Promise.all([
      opts.project !== undefined ? opts.project : loadProject(projectId),
      // Sprint numbers only — `{ priority }` rows for the Sprint options and the "+ New" number.
      // Never the task list: GET /tasks/facets `priorities` is project-wide whatever the caller
      // holds. Not fatal: without it the sprint table alone still yields options.
      opts.sprintRows || opts.tasks || api.get(`/projects/${projectId}/tasks/facets`)
        .then(r => (r.priorities || []).map(priority => ({ priority })))
        .catch(() => []),
      opts.members || api.get(`/projects/${projectId}/members`).then(r => r.members || []),
      opts.sprints || api.get(`/projects/${projectId}/sprints`).then(r => r.sprints || []),
      opts.statuses || loadStatuses(projectId),
    ]);
    state = { projectId, project, sprintRows, members, sprints, statuses };
  } catch (err) {
    if (isAlive()) showToast(err.message || 'Failed to load task form', 'error');
    return null;
  }
  if (!isAlive()) return null;
  const parentTask = opts.parentTask || opts.parentEpic || null;
  const draftScope = parentTask
    ? `${parentTask.type === 'epic' ? 'epic' : 'parent'}:${parentTask.id}`
    : null;
  // (C1512) Restore an in-progress draft for this project, if one survived a previous
  // accidental dismissal (Escape/backdrop/navigation) — see lib/task-draft.js. null when
  // there's nothing saved, or storage held only garbage.
  const draft = loadNewTaskDraft(state.projectId, draftScope);
  const restoredStatus = state.statuses.some(s => s.name === draft?.status) ? draft.status : null;
  const initialStatus = opts.status || restoredStatus || roleStatus(state.statuses, 'start')?.name || 'pending';
  const supportedTypes = ['task', 'story', 'bug'];
  const allowedTypes = Array.isArray(opts.allowedTypes)
    ? [...new Set(opts.allowedTypes)].filter(type => supportedTypes.includes(type))
    : supportedTypes;
  if (allowedTypes.length === 0) allowedTypes.push('task');
  const requestedType = draft?.type || opts.type || 'task';
  const initialType = allowedTypes.includes(requestedType) ? requestedType : allowedTypes[0];

  const singular = groupLabelSingular(state.project && state.project.task_group_label);
  // (C1512) A restored sprint number that no longer matches any real sprint/task priority
  // self-heals: sprintOptionsHtml() adds `currentPriority` to its option set whenever it
  // isn't already present, so this can never render a <select> with no selected option.
  // An explicit lane click (opts.priority > 0) always wins over a stale saved draft's sprint —
  // the user just told us which lane they meant.
  const { defaultPriority, restorePriority } = initialSprintPriority(opts.priority, state.sprints, draft?.priority);
  const sprintOpts = sprintOptionsHtml(state.sprints, state.sprintRows, restorePriority, singular);
  // C1334 — sprints hidden: no sprint field in the form; new tasks silently take
  // defaultPriority (an explicit sprint, else the newest sprint — a backlog parent's 0
  // included) — the same value the field would have pre-selected.
  const showSprintField = sprintsEnabled(state.project);

  let closed = false;
  let submitting = false;
  // (C1512) A restored assignee id is trusted only when it's still a real project member
  // (or members haven't loaded yet, in which case there's nothing to validate against) —
  // a member removed from the project since the draft was saved falls back to self, same
  // as the no-draft default below.
  const knownMemberIds = new Set(state.members.map(m => m.user_id || m.id));
  const restoredAssignee = draft && draft.assignee != null
    && (knownMemberIds.size === 0 || knownMemberIds.has(draft.assignee))
    ? draft.assignee : null;
  // New tasks default to the calling user in this form. Picking Unassigned remains
  // reachable because submit always sends the committed value, including null.
  let assignee = restoredAssignee ?? ((getUser() || {}).id ?? null);
  let assigneeCombo = null;
  let embedControls = null;
  let tagTypeahead = null;
  let depTypeahead = null;
  // (C1512) Attachments uploaded into THIS draft's description — tracked separately from
  // the description text itself (which only ever derives the visible thumbnail strip, see
  // lib/attachment-preview.js) so discardDraft() below deletes exactly what this modal
  // created, never an image/file ref the user hand-pasted in from somewhere else.
  let uploads = draft && draft.uploads ? draft.uploads.slice() : [];
  // Set at the top of onMount so persistDraft()/discardDraft() can read live field values
  // without threading `overlay` through every helper individually.
  let modalOverlay = null;
  // Own route cleanup as well as page cleanup: async routes may not run teardown.
  const onRouteChange = () => {
    if (routePath() !== initialRoute && modalOverlay?.isConnected) closeModal();
  };

  const body = `
    <form id="create-task-form">
      ${renderParentContext(parentTask)}
      ${opts.allowType ? `<div class="form-group">
        <label class="form-label" for="task-type">Type</label>
        <select class="form-select" id="task-type" name="type">
          ${allowedTypes.map(type => `<option value="${type}" ${type === initialType ? 'selected' : ''}>${type[0].toUpperCase() + type.slice(1)}</option>`).join('')}
        </select>
      </div>` : ''}
      <div class="form-group">
        <label class="form-label" for="task-status">Status</label>
        <select class="form-select" id="task-status" name="status">
          ${state.statuses.map(s => `<option value="${escapeHtml(s.name)}" ${s.name === initialStatus ? 'selected' : ''}>${escapeHtml(statusLabel(s.name))}</option>`).join('')}
        </select>
      </div>
      <div class="form-group">
        <label class="form-label" for="task-title">Title *</label>
        <input class="form-input" id="task-title" name="title" required maxlength="255">
      </div>
      <div class="form-group">
        <label class="form-label">Assignee</label>
        <div class="create-assignee-combo">${renderMemberCombo({ id: 'create-assignee', assignee, members: state.members })}</div>
      </div>
      <div class="form-group">
        <label class="form-label" for="task-desc">Description</label>
        <textarea class="form-textarea create-task-desc" id="task-desc" name="description"></textarea>
        <div class="md-preview create-task-desc-preview" id="task-desc-preview" title="Click to edit" hidden></div>
      </div>
      <div class="form-group">
        <label class="form-label" for="task-category">Category</label>
        <select class="form-select" id="task-category" name="category">
          <option value="CODING">Coding</option>
          <option value="HUMAN">Human</option>
        </select>
      </div>
      ${showSprintField ? `
      <div class="form-group">
        <label class="form-label" for="task-priority">${singular}</label>
        <select class="form-select" id="task-priority" name="priority">
          ${sprintOpts}
          <option value="new">+ New ${singular}</option>
          ${state.sprints.length === 0 ? `<option value="0" selected>${singular} 1 (priority 0)</option>` : ''}
        </select>
      </div>` : ''}
      <div class="form-group">
        <label class="form-label" for="task-tags">Tags</label>
        <input class="form-input" id="task-tags" placeholder="Add tag...">
      </div>
      <div class="form-group">
        <label class="form-label" for="task-deps">Depends on</label>
        <input class="form-input" id="task-deps" placeholder="Search tasks by key or title...">
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-ghost" id="modal-cancel">Cancel</button>
        <button type="submit" class="btn btn-primary" id="create-task-btn">Create Task</button>
      </div>
    </form>`;

  // (C1512) Snapshot every live field into localStorage. Bound to each field's own change
  // event from onMount, AFTER the draft-restore prefill runs there — so reopening a draft
  // can never immediately resave it back as empty.
  function persistDraft() {
    if (!modalOverlay || closed) return;
    const titleEl = modalOverlay.querySelector('#task-title');
    const descEl = modalOverlay.querySelector('#task-desc');
    const categoryEl = modalOverlay.querySelector('#task-category');
    const priorityEl = modalOverlay.querySelector('#task-priority');
    let priority = defaultPriority;
    if (showSprintField && priorityEl) {
      const n = parseInt(priorityEl.value, 10);
      // priorityEl.value === 'new' (unconfirmed "+ New Sprint") parses to NaN — fall back
      // to defaultPriority rather than persist a not-yet-real sprint number.
      if (Number.isFinite(n)) priority = n;
    }
    saveNewTaskDraft(state.projectId, {
      title: titleEl ? titleEl.value : '',
      description: descEl ? descEl.value : '',
      tags: tagTypeahead ? tagTypeahead.getTags() : [],
      dependencies: depTypeahead ? depTypeahead.getKeys() : [],
      category: categoryEl ? categoryEl.value : 'CODING',
      status: modalOverlay.querySelector('#task-status').value,
      assignee,
      priority,
      uploads,
      ...(opts.allowType ? { type: modalOverlay.querySelector('#task-type').value } : {}),
    }, draftScope);
  }

  // recordUpload({kind, url}) — passed as onUpload to both upload paths (paste/drop via
  // attachImageHandlers, the Embed picker via attachEmbedControls) so this modal always
  // knows exactly which attachments it created, regardless of which path produced them.
  function recordUpload(upload) {
    uploads.push(upload);
    persistDraft();
  }

  // discardDraft() — the user explicitly said "throw this away" (Cancel or the header ×,
  // wired in onMount below): erase the stored draft AND delete every attachment it
  // uploaded. Fire-and-forget — a 404 from an
  // already-deleted row is fine, there's nothing useful to do with a failure here.
  function discardDraft() {
    if (submitting) return;
    clearNewTaskDraft(state.projectId, draftScope);
    for (const u of uploads) {
      const path = attachmentDeletePath(u.url);
      if (path) api.delete(path).catch(() => {});
    }
    uploads = [];
  }

  return openModal('New Task', body, {
    onMount(overlay) {
      modalOverlay = overlay; // (C1512) lets persistDraft()/discardDraft() read live fields
      window.addEventListener('hashchange', onRouteChange);

      const comboRoot = overlay.querySelector('.create-assignee-combo');
      assigneeCombo = createMemberCombo(comboRoot, {
        getMembers: () => state.members, // live — page-load members fetch may still be in flight
        getValue: () => assignee,
        setValue: (id) => { assignee = id; persistDraft(); }, // (C1512)
        onCommit: () => {}, // nothing to PATCH until submit — the row isn't finalized yet
        isAlive: () => document.body.contains(comboRoot),
        // overlay is ui.js's fixed full-viewport .modal-overlay; the real scroller is
        // its .modal child (max-height: 85vh; overflow-y: auto) — same as the Add Child
        // modal's combo in task-detail.js.
        scrollEl: overlay.querySelector('.modal') || overlay,
      });

      // Existing tags only (C1511) — a name typed here that isn't already registered has
      // no way to become a chip, so the create request below can never 400 with
      // TAGS_UNREGISTERED (resolveTagIds(), api/src/lib/task-service.js) again. Creating a
      // brand-new tag stays a task-detail-panel action (its own typeahead instance passes
      // allowCreate:true) — this modal has no room for the required-description form.
      tagTypeahead = attachTagTypeahead(overlay.querySelector('#task-tags'), {
        projectId: state.projectId,
        initialTags: draft && draft.tags ? draft.tags : [], // (C1512) restore draft chips
        scrollEl: overlay.querySelector('.modal') || overlay,
        allowCreate: false,
        onChange: () => persistDraft(), // (C1512)
        isAlive: () => document.body.contains(overlay),
      });

      // Depends on (TPT598) — candidates come from an on-demand server search, so a task
      // outside the caller's rows (e.g. the board's filtered page) is still reachable.
      depTypeahead = attachDependencyTypeahead(overlay.querySelector('#task-deps'), {
        projectId: state.projectId,
        initialKeys: draft && draft.dependencies ? draft.dependencies : [],
        scrollEl: overlay.querySelector('.modal') || overlay,
        onChange: () => persistDraft(),
        isAlive: () => document.body.contains(overlay),
      });

      // Wire paste/drop upload immediately. New-task attachments have no task key until
      // the create request commits; their markdown URLs still identify the stored rows.
      // allowFiles:true (C1447) — this field also gets the Embed ▾ picker below, so
      // dropping/pasting a non-image file must work too, not just images.
      const descEl = overlay.querySelector('#task-desc');
      if (descEl) attachImageHandlers(descEl, state.projectId, null, { allowFiles: true, onUpload: recordUpload });

      // Markdown edit/preview toggle (C1446) — mirrors task-detail.js's click-to-edit/
      // blur-to-render pattern, but keeps `descEl` in the DOM (never replaceWith) since
      // the submit handler below reads it as `form.description.value`: swapping it out
      // for a rendered div would make that a form.elements miss. Preview shown only on
      // blur with non-empty content — an empty preview box in a create form is just noise.
      const previewEl = overlay.querySelector('#task-desc-preview');
      if (descEl && previewEl) {
        descEl.addEventListener('blur', () => {
          if (!descEl.value.trim()) return;
          // Pin the swapped-in element's height to the outgoing one's BEFORE toggling
          // hidden, in both directions — the Create Task button sits right below this
          // field, and blur fires on the button's own mousedown; any height jump here
          // would move the button out from under the still-in-flight click.
          previewEl.style.height = descEl.offsetHeight + 'px';
          previewEl.innerHTML = renderMarkdown(descEl.value);
          descEl.hidden = true;
          previewEl.hidden = false;
        });
        previewEl.addEventListener('click', () => {
          descEl.style.height = previewEl.offsetHeight + 'px';
          previewEl.hidden = true;
          descEl.hidden = false;
          descEl.focus();
          const len = descEl.value.length;
          descEl.setSelectionRange(len, len);
        });
        // Async image upload (lib/image-paste.js) rewrites descEl.value and dispatches
        // 'input' after blur may already have rendered the preview from the placeholder
        // text — re-render so the preview never shows a stale "uploading..." ref.
        descEl.addEventListener('input', () => {
          if (!previewEl.hidden) previewEl.innerHTML = renderMarkdown(descEl.value);
        });
      }

      // Embed ▾ button + live thumbnail strip (C1447) — anchored after previewEl (not
      // descEl) so the block sits below whichever of textarea/preview C1446's toggle is
      // currently showing, rather than wedging between them. Kept visible through both
      // toggle states rather than hidden on blur: hiding it would shrink the field by the
      // strip's height at the exact moment blur fires (the Create button's own
      // mousedown, line ~193 above) — the same button-jump C1446's height-pinning exists
      // to prevent, reintroduced one element lower.
      if (descEl && previewEl) {
        embedControls = attachEmbedControls(descEl, {
          projectId: state.projectId,
          taskKey: null,
          anchor: previewEl,
          scrollEl: overlay.querySelector('.modal') || overlay,
          onUpload: recordUpload, // (C1512)
          // Import ▾ (C1448) — Task App sub-nav order (Jira / Trello / Gmail). All three
          // now open their own picker: Trello (C1448), Gmail (C1449), Jira (C1450).
          imports: [
            { action: 'jira', label: 'From Jira' },
            { action: 'trello', label: 'From Trello' },
            { action: 'gmail', label: 'From Gmail' },
          ],
          onImport: (action) => {
            // .member-combo-dropdown is z-index 320, above the picker's 260 — with
            // .embed-block's mousedown->preventDefault the assignee input never blurs on
            // its own, so an open combo would paint over the picker. Same for the tag
            // typeahead's own dropdown (z-index 320 too).
            assigneeCombo?.close();
            tagTypeahead?.close();
            depTypeahead?.close();
            const onPick = (payload) => applyImportPrefill(overlay, payload);
            if (action === 'trello') { openTrelloCardPicker(state.projectId, { onPick }); return; }
            if (action === 'gmail') { openGmailThreadPicker({ onPick }); return; }
            if (action === 'jira') { openJiraIssuePicker({ onPick }); return; }
          },
        });
      }

      // (C1512) Restore prefill — placed after attachEmbedControls() above so the
      // synthetic 'input' dispatch below reaches its 'input' listener
      // (renderAttachmentStrip) and repaints the thumbnail strip for any restored
      // image/file refs already sitting in the description text.
      const titleEl = overlay.querySelector('#task-title');
      const categoryEl = overlay.querySelector('#task-category');
      if (draft) {
        if (draft.title) titleEl.value = draft.title;
        if (draft.category) categoryEl.value = draft.category;
        if (draft.description && descEl) {
          descEl.value = draft.description;
          descEl.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      // (C1512) Persist on every field change — bound AFTER the restore above, so
      // reopening a draft can never immediately resave it back as empty.
      overlay.querySelector('#task-type')?.addEventListener('change', persistDraft);
      overlay.querySelector('#task-status').addEventListener('change', persistDraft);
      titleEl.addEventListener('input', persistDraft);
      categoryEl.addEventListener('change', persistDraft);
      if (descEl) descEl.addEventListener('input', persistDraft);
      if (showSprintField) overlay.querySelector('#task-priority')?.addEventListener('change', persistDraft);

      overlay.querySelector('#modal-cancel').addEventListener('click', () => { discardDraft(); closeModal(); });
      // (C1512) Capture-phase — openModal() registers #modal-close's own bubble-phase
      // `click -> closeModal()` listener BEFORE onMount runs, so a bubble-phase listener
      // added here would fire too late (closeModal() already ran). Capture always
      // completes before any bubble-phase handler anywhere fires, regardless of
      // registration order — same trick embed-menu.js uses for its Escape handler. The ×
      // is an explicit "I'm done with this" action, same as Cancel: erase the draft.
      overlay.addEventListener('click', (e) => {
        if (e.target.closest('#modal-close')) discardDraft();
      }, true);
      overlay.querySelector('#create-task-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        if (submitting || closed) return;

        const form = e.target;
        let priority;
        if (!showSprintField) {
          // C1334 — field hidden → submit exactly what it would have pre-selected (the
          // visible field would have pre-selected. Never let a hidden control silently
          // change what gets submitted.
          priority = defaultPriority;
        } else {
          const priorityVal = form.priority.value;
          if (priorityVal === 'new') {
            // One past every listed sprint: an empty sprint-table row can sit above the
            // highest in-use number, so the in-use numbers alone could name a listed sprint.
            priority = nextSprintNumber(state.sprints, state.sprintRows);
          } else {
            priority = parseInt(priorityVal, 10);
          }
        }
        const tagsRaw = tagTypeahead ? tagTypeahead.getTags() : [];
        const deps = depTypeahead ? depTypeahead.getKeys() : [];

        const btn = overlay.querySelector('#create-task-btn');
        btn.disabled = true;
        btn.textContent = 'Creating...';
        submitting = true;

        try {
          // task_key is deliberately omitted. POST allocates and inserts it in one
          // transaction, so opening, canceling, or failing validation consumes no key.
          const { task } = await api.post(`/projects/${state.projectId}/tasks`, {
            title: form.title.value.trim(),
            description: form.description.value,
            category: form.category.value,
            priority,
            tags: tagsRaw.length ? tagsRaw : undefined,
            dependencies: deps.length ? deps : undefined,
            // Always sent (never conditional) so picking Unassigned writes null.
            assignee,
            status: form.status.value,
            type: opts.allowType ? form.type.value : 'task',
            parent_id: parentTask?.id ?? null,
            is_objective: !parentTask,
          });
          // (C1512) Clear only — never discardDraft(): the uploads tracked in `uploads`
          // now belong to the just-created task and must survive.
          clearNewTaskDraft(state.projectId, draftScope);
          if (!closed) closeModal();
          showToast('Task created', 'success');
          if (isAlive()) await opts.onCreated?.(task);
        } catch (err) {
          btn.disabled = false;
          btn.textContent = 'Create Task';
          showToast(err.message || 'Failed to create task', 'error');
        } finally {
          submitting = false;
        }
      });
    },
    onClose() {
      closed = true;
      window.removeEventListener('hashchange', onRouteChange);
      // Destroy first, unconditionally — leaked scroll/resize listeners + a stray
      // body-appended dropdown node would otherwise survive a successful create too
      // (mirrors task-detail.js's Add Child modal onClose).
      if (assigneeCombo) { assigneeCombo.destroy(); assigneeCombo = null; }
      if (tagTypeahead) { tagTypeahead.destroy(); tagTypeahead = null; }
      if (depTypeahead) { depTypeahead.destroy(); depTypeahead = null; }
      if (embedControls) { embedControls.destroy(); embedControls = null; }
      // Orphan-overlay guard (C1448, generalized C1449): if this modal is closing while an
      // Import ▾ picker (Trello, Gmail, Jira) is still open above it (Escape reaching this
      // modal, backdrop click, route navigation), nothing else would tear the picker down
      // — it would survive as a full-viewport overlay with a live capture-phase Escape
      // handler. The shared singleton in lib/import-picker-shell.js means this one call
      // covers every picker kind. No-ops if none open.
      closeImportPicker();
    },
  });
}
